// HowItWorksPage.jsx
// Landing Page: How VivaJour Works - Product Explainer
// Slug: /how-it-works

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'How VivaJour Works | Automatic Trip Detection & On-Device Processing',
  description: 'See how VivaJour transforms your camera roll into organized travel albums. Automatic trip detection using GPS metadata, on-device photo processing, and professional album templates. No cloud uploads required.',
  slug: '/how-it-works'
};

export default function HowItWorksPage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link active">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">⚙️ Product Guide</span>
        <h1>How VivaJour Works</h1>
        <p className="landing-hero-subtitle">
          From camera roll to shareable travel album — all processed on your device. Here's the complete picture of what happens when you use VivaJour.
        </p>
      </section>

      {/* Overview */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2>The Big Picture</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '16px', lineHeight: '1.7', marginTop: '20px' }}>
            VivaJour reads the metadata already embedded in your photos — dates, times, and GPS coordinates — to automatically understand where you traveled and when. This information is processed entirely on your device to organize your photos into a coherent travel story.
          </p>
          <p style={{ color: 'var(--landing-text)', fontSize: '16px', lineHeight: '1.7', marginTop: '16px', fontWeight: 600 }}>
            All photo processing, organization, and curation happens on your device. Your photos only leave your device when you explicitly choose to preview or share them.
          </p>
        </div>
      </section>

      {/* Step-by-Step Process */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>Step-by-Step Process</h2>
          
          <div className="landing-steps" style={{ marginTop: '40px' }}>
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Select Photos</h3>
                <p>Choose photos from your camera roll. You can select manually, choose a date range, or let VivaJour detect trips automatically based on travel patterns in your photo metadata.</p>
                <p className="landing-step-note">VivaJour accesses your photos through standard iOS/Android photo libraries. We request only the permissions needed to read metadata.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Metadata Extraction</h3>
                <p>VivaJour reads the EXIF data embedded in each photo: capture date, time, and GPS coordinates (if available). This happens instantly on your device.</p>
                <p className="landing-step-note">Photos without GPS data are organized by date. Photos without date data use file creation time as a fallback.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Location Clustering</h3>
                <p>GPS coordinates are grouped into locations using proximity algorithms. Photos taken near each other become part of the same "stop" in your journey.</p>
                <p className="landing-step-note">All clustering calculations run on-device. No location data is sent to servers.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Place Name Resolution</h3>
                <p>GPS coordinates are converted to place names (e.g., "Paris, France" or "Central Park"). This uses your device's built-in geocoding services.</p>
                <p className="landing-step-note">Place names may use cached data or online system services provided by iOS/Android. This is the only step that may use a network connection — and it is handled by your operating system, not VivaJour servers.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">5</div>
              <div className="landing-step-content">
                <h3>Story Structure</h3>
                <p>VivaJour organizes your photos into a narrative structure: grouped by day and location, ordered chronologically, creating a natural flow from arrival to departure.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">6</div>
              <div className="landing-step-content">
                <h3>Album Generation</h3>
                <p>Your organized photos are placed into an album template. You see a preview instantly and can edit, reorder, add captions, or change layouts.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">7</div>
              <div className="landing-step-content">
                <h3>Export & Share</h3>
                <p>Export your album as a PDF for local saving or sharing. Preview your album in Story Mode or generate a shareable link for friends and family. Your photos only leave your device when you explicitly choose to preview or share.</p>
                <p className="landing-step-note">Preview and sharing use secure cloud storage (Firebase) to make your album accessible. You control what gets uploaded and can delete shared albums at any time.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes It Work */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>What Makes VivaJour Different</h2>
          <p className="landing-section-subtitle">
            The technology behind effortless travel albums.
          </p>
          
          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔒</div>
              <h3>Privacy-First Processing</h3>
              <p>All photo organization and editing happens entirely on your device. No cloud uploads for processing means your photos stay private.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">⚡</div>
              <h3>Instant Organization</h3>
              <p>Smart algorithms read your photo metadata and automatically group by location and time. Your album structure appears in seconds.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📍</div>
              <h3>Intelligent Location Detection</h3>
              <p>GPS coordinates automatically cluster into meaningful places. Paris becomes Paris, not scattered random coordinates.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">💾</div>
              <h3>Offline Capable</h3>
              <p>Create and edit albums without internet. Everything works locally until you're ready to share.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎯</div>
              <h3>No Manual Tagging Required</h3>
              <p>Your photos already contain the information needed. VivaJour reads what's there — you don't have to tag or categorize anything.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔐</div>
              <h3>You Control Sharing</h3>
              <p>Photos only leave your device when you explicitly choose to preview or share. Delete shared content anytime.</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/technical-details" className="landing-btn landing-btn-secondary">
              View Technical Details →
            </Link>
          </div>
        </div>
      </section>

      {/* What Requires Network */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>What Works Offline vs. Online</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '40px' }}>
            <div className="landing-highlight-box">
              <h3 style={{ color: '#22c55e' }}>✓ Works Offline</h3>
              <ul>
                <li>Photo selection from camera roll</li>
                <li>Metadata extraction (dates, GPS)</li>
                <li>Location clustering</li>
                <li>Trip organization</li>
                <li>Album building and editing</li>
                <li>Caption editing</li>
                <li>PDF generation</li>
              </ul>
            </div>
            
            <div className="landing-highlight-box">
              <h3 style={{ color: '#f59e0b' }}>○ Requires Network</h3>
              <ul>
                <li>Place name resolution (via OS geocoding)</li>
                <li>Album preview / Story Mode viewing</li>
                <li>Sharing albums via link</li>
                <li>Exporting to external services</li>
              </ul>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '16px' }}>
                Network usage is either handled by your operating system (geocoding) or explicitly initiated by you (preview and sharing).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Two Ways to Build Albums</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 40px' }}>
            Same processing, different workflows.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            <Link to="/current-trip" className="landing-feature-card" style={{ textDecoration: 'none' }}>
              <div className="landing-feature-icon">📍</div>
              <h3>Current Trip Mode</h3>
              <p>Build your album while traveling. Add photos daily. Watch your story grow in real-time. Share highlights before you get home.</p>
              <span style={{ color: 'var(--landing-accent)', fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>Learn about Current Trip →</span>
            </Link>
            
            <Link to="/albums" className="landing-feature-card" style={{ textDecoration: 'none' }}>
              <div className="landing-feature-icon">📚</div>
              <h3>Past Trip Mode</h3>
              <p>Organize trips from your camera roll after the fact. Select photos, let VivaJour detect structure, customize your album.</p>
              <span style={{ color: 'var(--landing-accent)', fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>Learn about Albums →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>
          
          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How accurate is the automatic trip detection?</h4>
              <p className="landing-faq-answer">Trip detection works best when your photos have GPS data. Most modern smartphones embed location data automatically. Photos without GPS are organized by date, which usually works well for travel timelines.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What if my photos don't have GPS data?</h4>
              <p className="landing-faq-answer">VivaJour will organize photos by date. You can manually assign locations or group photos by adding section headers.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How fast is album generation?</h4>
              <p className="landing-faq-answer">Most albums generate in under a minute. Processing time depends on the number of photos and your device's capabilities. Newer phones are faster.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I edit the album after it's generated?</h4>
              <p className="landing-faq-answer">Yes. Full editing control: reorder photos, add/remove images, edit captions, change layouts, adjust section headers.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What photo formats are supported?</h4>
              <p className="landing-faq-answer">VivaJour supports JPEG, PNG, and HEIC (the default format on iPhones). HEIC photos are converted automatically during processing.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Is there a photo limit?</h4>
              <p className="landing-faq-answer">No hard limit, but very large albums (500+ photos) may take longer to process. We recommend curating your best shots for the best experience.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">When do my photos leave my device?</h4>
              <p className="landing-faq-answer">All album creation, organization, and editing happens entirely on your device. Your photos only leave your device when you explicitly choose to preview your album or share it via link. These actions use secure cloud storage (Firebase) and you maintain full control — you can delete shared albums at any time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Reminder */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow" style={{ textAlign: 'center' }}>
          <div className="landing-feature-icon" style={{ margin: '0 auto 20px', width: '64px', height: '64px', fontSize: '32px' }}>🔒</div>
          <h2>Built With On-Device Processing</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '16px', lineHeight: '1.7', marginTop: '16px' }}>
            Every step described above — from metadata extraction to album generation — happens on your device. We designed VivaJour this way because your travel photos deserve privacy.
          </p>
          <div style={{ marginTop: '28px' }}>
            <Link to="/privacy-first" className="landing-btn landing-btn-secondary">
              Learn About Our Privacy Approach →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Ready to Try It?</h3>
            <p>See VivaJour in action with your own photos.</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on Android
              </a>
            </div>
          </div>
          
          {/* Internal Links */}
          <div className="landing-internal-links">
            <Link to="/privacy-first" className="landing-internal-link">
              🔒 Privacy-First — How we protect your data
            </Link>
            <Link to="/current-trip" className="landing-internal-link">
              📍 Current Trip — Build albums while traveling
            </Link>
            <Link to="/templates" className="landing-internal-link">
              🎨 Templates — Professional album layouts
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your photos stay on your device. Your memories stay yours.
        </p>
      </footer>
    </div>
  );
}
